import { useState, useEffect, useCallback, useRef } from 'react'
import { fmtMonto, hoyISO } from '../utils/fecha'
import { useNavigate, useParams } from 'react-router-dom'
import { notasVentaApi, clientesApi } from '../api/notasVenta'
import BuscadorCliente from '../components/BuscadorCliente'
import BuscadorArticulo from '../components/BuscadorArticulo'
import Modal from '../components/Modal'
import PasteExcelModal from '../components/PasteExcelModal'
import ModalAltaCliente from '../components/ModalAltaCliente'
import ModalCondicionFiscal from '../components/ModalCondicionFiscal'
import ModalPreciosArticulo from '../components/ModalPreciosArticulo'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import ModalListaClientes from '../components/ModalListaClientes'
import ModalListaVendedores from '../components/ModalListaVendedores'
import NotaClienteBanner from '../components/NotaClienteBanner'
import { listasApi } from '../api/listaPrecios'
import api from '../api/client'
import { useAuth } from '../context/AuthContext'
import { etiquetaCliente } from '../utils/cliente'
import useDirtyGuard from '../hooks/useDirtyGuard'
import Boton from '../components/Boton'
import { PROVINCIAS, esCapitalFederal, matchProvincia } from '../data/argentina'
import CampoLocalidad from '../components/CampoLocalidad'
import CampoDireccion from '../components/CampoDireccion'
import CotizadorEnvio from '../components/CotizadorEnvio'
import { destinosParaPayload, destinosDesdeApi, repartoDe } from '../utils/envioDestinos'
import MensajesNVCard from '../components/MensajesNVCard'
import CeldaStockPresea, { CabeceraStockPresea } from '../components/CeldaStockPresea'
import useStockPresea from '../hooks/useStockPresea'
import { useConsultasDeposito, RespuestaDeposito, PedirStockDepositoModal } from '../components/StockDeposito'
import { conteoItems, textoConteo } from '../utils/conteoItems'
import { listaParaBuscar } from '../utils/listaVenta'

// Las membresías del Wine Club (WC00001…WC00005). Mismo prefijo que
// config/wine_club.php en el backend.
const PREFIJO_MEMBRESIA = 'WC000'

const DIR_ENVIO_VACIA = { receptor_nombre: '', telefono: '', calle: '', piso: '', localidad: '', provincia: '', codigo_postal: '' }

// Renglones de SERVICIO: su precio no sale nunca del maestro de artículos.
// El envío lo cotiza EnvioWebService por peso y zona; la gift card vale lo que
// el cliente pagó. Los dos tienen un precio cargado en LISTA1 ($13.800 y $2.500)
// que existe solo para que Presea pueda facturar el código, y que NO es el
// precio de ninguna operación real. Cinturón además de `precio_manual`: si la
// bandera se pierde en algún camino, estos códigos igual quedan protegidos.
const esPrecioPropio = (it) => {
  const cod = (it.articulo?.codigo_alfa || '').toUpperCase()
  return cod === 'ENV0000' || cod.startsWith('GIF')
}

const FORM_VACIO = {
  cliente_id: null,
  cliente: null,
  vendedor_id: '',
  vendedor_codigo: '',
  vendedor_nombre: '',
  // La lista con la que se vendió (solo edición; en alta la define el flujo).
  lista_precios: '',
  fecha: hoyISO(),
  fecha_entrega_est: '',
  horario_entrega: '',
  notas_entrega: '',
  indicaciones_packaging: '',
  orden_compra_cliente: '',
  // Si dirEnvio_usar_cliente=true se manda direccion_envio=null (el sistema lee del cliente).
  // Si false, se persiste el objeto. Mismo criterio que el modal "Enviar paquete".
  dirEnvio_usar_cliente: true,
  dirEnvio_retira: null,   // objeto {retira:true,…} del pedido, se conserva tal cual
  direccion_envio: DIR_ENVIO_VACIA,
  envio_destinos: [],      // envío múltiple (T80) — docs/diseno-envio-multiple.md
  condicion_venta: '1',   // CONTADO por defecto (el más común)
  deposito: '100',        // depósito 100 salvo que elijan otro
  observaciones: '',
  descuento_gral: '',
  // Canje MKT (solo administradores): sin entrega, sin recibo, vendedor 1.
  es_mkt: false,
  items: [],
}

export default function NotaVentaFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const esEdicion = Boolean(id)
  const { user, hasAnyRole } = useAuth()
  const esAdmin = hasAnyRole(['administrador', 'super_administrador'])

  const [form, setForm] = useState(FORM_VACIO)
  const [condiciones, setCondiciones] = useState([])
  const [cargando, setCargando] = useState(esEdicion)
  const [guardando, setGuardando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [errores, setErrores] = useState({})
  const [mensajeOk, setMensajeOk] = useState('')
  // Percepciones estimadas (solo clientes RI): misma pieza de cálculo que la
  // proforma y el checkout, contra la ficha local. Informativas — el importe
  // final lo fija Presea al facturar.
  const [percep, setPercep] = useState(null)
  // Nota del checkout (Magento/web): solo lectura acá, pero el vendedor tiene
  // que verla mientras edita el pedido — es una instrucción del comprador.
  const [notaCliente, setNotaCliente] = useState('')
  const [envioFuera, setEnvioFuera] = useState(false)

  // Trabajo sin guardar: una NV a medio cargar no se pierde con F5 ni con un
  // click al sidebar (el guard se apaga mientras guarda / tras guardar OK).
  useDirtyGuard(!guardando && !mensajeOk && (form.items.length > 0 || !!form.cliente_id))

  // C3 — F2 / lupa
  const [articuloBuscandoIdx, setArticuloBuscandoIdx] = useState(null)

  // C2 — paste from Excel
  const [pasteText, setPasteText] = useState('')
  const [showPasteModal, setShowPasteModal] = useState(false)

  // C4 — alta cliente
  const [showAltaCliente, setShowAltaCliente] = useState(false)
  const [showCondFiscal, setShowCondFiscal] = useState(false)
  const [showEditarCliente, setShowEditarCliente] = useState(false)

  // F6 — buscador de cliente via teclado
  const [showBuscadorClienteModal, setShowBuscadorClienteModal] = useState(false)

  // Buscar artículos multi-selección
  const [showBuscarArticulos, setShowBuscarArticulos] = useState(false)
  const [showVendedoresModal, setShowVendedoresModal] = useState(false)

  // Stock EN VIVO desde Presea (un depósito por columna) — hook compartido con el
  // presupuesto: se pide con el botón "Consultar stock" y se pinta por renglón.
  const stockPresea = useStockPresea()
  const consultarStock = () => stockPresea.consultar(form.items.map(it => it.articulo?.codigo_alfa))
  // Consulta de stock a depósito: la respuesta vuelve a esta NV (columna Depósito).
  const deposito = useConsultasDeposito(esEdicion ? id : null)
  const [showPedirDeposito, setShowPedirDeposito] = useState(false)

  // Lista de precios:
  //  - Si el USUARIO tiene lista asignada → se usa esa (precio directo, filtra artículos).
  //  - Al EDITAR, manda la lista CON LA QUE SE VENDIÓ (nv.lista_precios) —
  //    regla de Martin, 2 sep 2026 (caso LGR-20260901-JSZG8Q): una NV del
  //    store es LISTA1 aunque la ficha del cliente diga LISTADIST; abrirla
  //    no puede cambiarle el canal ni cotizar renglones con otra lista.
  //  - Si no hay nada de eso → la lista del cliente (alta manual).
  const userLista = user?.lista_precios || null
  const listaEfectiva = userLista || form.lista_precios || form.cliente?.lista_precios || null

  // Precios de la lista efectiva.
  //
  // REGLA (04/08/2026): el precio de un renglón se fija cuando se carga y NO se
  // vuelve a calcular solo. Recotizar es una decisión del operador —cambiar la
  // lista de precios—, nunca un efecto de abrir una pantalla.
  //
  // Antes este efecto corría también al MONTAR el form de edición (cuando llega
  // el cliente y con él su lista) y pisaba TODOS los renglones con el precio del
  // maestro. Así se facturó de menos dos veces el mismo día: el envío cotizado
  // en $16.379 quedó en $13.800 —el precio fijo de ENV0000 en LISTA1— con solo
  // abrir la NV y guardarla. Es plata cambiando sola, sin que nadie lo decida.
  const [preciosLista, setPreciosLista] = useState({})
  const listaMontada = useRef(null)
  useEffect(() => {
    if (!listaEfectiva) { setPreciosLista({}); return }
    // Primera lista que ve una NV existente = la que ya tenía. Traemos los
    // precios (el form los necesita para agregar renglones nuevos) pero NO se
    // toca ni un renglón cargado.
    const esCargaInicial = esEdicion && listaMontada.current === null
    listaMontada.current = listaEfectiva

    api.get(`/listas-precios/codigo/${encodeURIComponent(listaEfectiva)}/items`).then(({ data }) => {
      const mapa = {}
      const items = Array.isArray(data) ? data : (data.data || [])
      items.forEach(it => { mapa[it.articulo_id] = parseFloat(it.precio) })
      setPreciosLista(mapa)
      if (esCargaInicial) return

      setForm(f => ({ ...f, items: f.items.map(it => {
        // Aun recotizando a propósito, hay renglones que NUNCA salen del
        // maestro: el envío lo cotiza EnvioWebService por peso y zona, y la
        // gift card vale lo que el cliente pagó. Más los precios manuales o
        // negociados, que son una decisión ya tomada.
        if (it.precio_manual || esPrecioPropio(it)) return it
        const p = mapa[it.articulo_id]
        return p != null ? { ...it, precio_lista: p, precio_manual: false } : it
      }) }))
    }).catch(() => setPreciosLista({}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listaEfectiva])

  useEffect(() => {
    api.get('/condiciones-venta')
      .then(({ data }) => setCondiciones(Array.isArray(data) ? data : (data.data || [])))
      .catch(() => setCondiciones([]))
  }, [])

  // En una NV nueva el vendedor arranca siendo quien la está cargando: es lo
  // que pasa casi siempre, y tener que tipear el código a mano hacía que
  // quedara vacío o equivocado (la NV 47 salió con el código 1 en vez del 9,
  // y la 46 sin código). Se puede cambiar con la lupa, como antes.
  //
  // Va en un efecto y no en el estado inicial porque el usuario llega después
  // del primer render. Solo completa lo que está vacío: nunca pisa lo elegido.
  useEffect(() => {
    if (esEdicion || !user?.id) return

    setForm(f => {
      if (f.vendedor_id && f.vendedor_codigo) return f

      return {
        ...f,
        vendedor_id: f.vendedor_id || user.id,
        vendedor_codigo: f.vendedor_codigo || (user.codigo_vendedor ? String(user.codigo_vendedor) : ''),
        vendedor_nombre: f.vendedor_nombre || (user.codigo_vendedor ? (user.nombre || '') : ''),
      }
    })
  }, [esEdicion, user])

  // Libreta de direcciones del cliente: las direcciones de envío que ya se
  // usaron en NVs anteriores (se aprenden solas al guardar). El selector de la
  // sección de envío las ofrece para no re-tipear la del hijo / la oficina.
  const [dirGuardadas, setDirGuardadas] = useState([])
  useEffect(() => {
    if (!form.cliente_id) { setDirGuardadas([]); return }
    api.get(`/clientes/${form.cliente_id}/direcciones`)
      .then(({ data }) => setDirGuardadas(Array.isArray(data) ? data : []))
      .catch(() => setDirGuardadas([]))
  }, [form.cliente_id])

  // Artículo para el que se muestra el selector de precios por lista (usuario sin lista)
  const [articuloPrecios, setArticuloPrecios] = useState(null)

  // Tras asignar lista: el effect anterior se dispara por cambio de form.cliente y recalcula
  // los precios de los items ya cargados (en handleAsignarLista llamamos a recalcular).

  // C2 — detect Ctrl+V outside inputs at document level
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return
      const text = e.clipboardData?.getData('text') || ''
      if (text.includes('\t')) {
        e.preventDefault()
        setPasteText(text)
        setShowPasteModal(true)
      }
    }
    document.addEventListener('paste', handler)
    return () => document.removeEventListener('paste', handler)
  }, [])

  // F6 — open client search modal from anywhere
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'F6') { e.preventDefault(); setShowBuscadorClienteModal(true) }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (!esEdicion) return
    notasVentaApi.obtener(id).then(({ data }) => {
      const dirCargada = data.direccion_envio || null
      setNotaCliente(data.nota_cliente || '')
      setForm({
        cliente_id: data.cliente.id,
        cliente: data.cliente,
        vendedor_id: data.vendedor?.id || '',
        vendedor_codigo: data.vendedor_codigo || '',
        vendedor_nombre: '',
        lista_precios: data.lista_precios || '',
        fecha: data.fecha,
        fecha_entrega_est: data.fecha_entrega_est || '',
        horario_entrega: data.horario_entrega || '',
        notas_entrega: data.notas_entrega || '',
        indicaciones_packaging: data.indicaciones_packaging || '',
        orden_compra_cliente: data.orden_compra_cliente || '',
        // Solo receptor/teléfono sin calle = "a la dirección del cliente, lo
        // recibe otra persona": el tilde queda puesto y el receptor visible.
        dirEnvio_usar_cliente: !dirCargada || (!dirCargada.calle && !dirCargada.localidad),
        // RETIRO EN SUCURSAL (pedido web/Magento): la marca viaja intacta al
        // guardar — este form la pisaba con null y el pedido "volvía" a envío
        // al domicilio fiscal (caso LGR-20260831-TZ9VWJ, 31 ago 2026).
        dirEnvio_retira: dirCargada?.retira === true ? dirCargada : null,
        direccion_envio: dirCargada
          ? { ...DIR_ENVIO_VACIA, ...Object.fromEntries(Object.entries(dirCargada).map(([k, v]) => [k, v ?? ''])) }
          : DIR_ENVIO_VACIA,
        envio_destinos: destinosDesdeApi(data.envio_destinos),
        condicion_venta: data.condicion_venta || '',
        deposito: data.deposito || '',
        observaciones: data.observaciones || '',
        descuento_gral: parseFloat(data.descuento_gral) || 0,
        es_mkt: !!data.es_mkt,
        items: data.items.map(it => ({
          articulo_id: it.articulo_id,
          articulo: it.articulo,
          cantidad: it.cantidad,
          precio_lista: parseFloat(it.precio_lista),
          descuento: parseFloat(it.descuento) || 0,
          observacion: it.observacion || '',
          descripcion: it.descripcion || '',
          // Turno de una experiencia (lo eligió el comprador): se conserva
          // al re-guardar, Ventas arma las listas con esto.
          turno: it.turno || '',
          restriccion: it.restriccion || '',
          // El renglón trae su precio PROPIO (envío cotizado, monto de la gift
          // card, precio negociado, promo Magento horneada). Sin esta bandera
          // el efecto de la lista de precios lo pisaba con el del maestro al
          // abrir la NV para editar — el envío de $16.379 quedaba en $13.800,
          // que es lo que vale ENV0000 en LISTA1 (04/08/2026).
          precio_manual: !!it.precio_modificado,
        })),
      })
      // Si la NV tiene vendedor_codigo seteado, traemos el nombre para mostrarlo.
      if (data.vendedor_codigo) {
        api.get(`/vendedores/${data.vendedor_codigo}`)
          .then(({ data: v }) => setForm(f => ({ ...f, vendedor_nombre: v.nombre })))
          .catch(() => {})
      }
      setCargando(false)
    }).catch(() => navigate('/notas-venta'))
  }, [id, esEdicion, navigate])

  // Agrega un ítem con un precio dado (vacío/null → precio manual)
  // Sugerencia #66 (Nicolás Garcés): al facturar una membresía A MANO, el
  // sistema ofrece sumar los vinos del mes con 100% de descuento — los mismos
  // renglones que arma sola la renovación automática. Se SUGIERE, no se agrega
  // por su cuenta: quién factura decide.
  const [sugerenciaWc, setSugerenciaWc] = useState(null)

  const pedirSugerenciaWineClub = async (articulo) => {
    const sku = String(articulo.codigo_alfa || '').toUpperCase()
    if (!sku.startsWith(PREFIJO_MEMBRESIA)) return
    try {
      const { data } = await api.get('/wine-club/sugerencia-nv', {
        params: { sku, cliente_id: form.cliente_id || undefined },
      })
      if (data?.nivel) setSugerenciaWc(data)
    } catch { /* la sugerencia nunca frena la carga de la NV */ }
  }

  const sumarVinosDelClub = () => {
    const nuevos = (sugerenciaWc?.items || []).map(it => ({
      articulo_id: it.articulo_id,
      articulo: { id: it.articulo_id, codigo_alfa: it.codigo_alfa, descripcion: it.descripcion },
      cantidad: 1,
      precio_lista: it.precio_lista || '',
      descuento: 100,
      observacion: '',
      precio_manual: false,
    }))
    setForm(f => ({ ...f, items: [...f.items, ...nuevos] }))
    setSugerenciaWc(null)
  }

  const agregarItemConPrecio = (articulo, precio) => {
    const sinPrecio = precio == null
    setForm(f => ({
      ...f,
      items: [...f.items, {
        articulo_id: articulo.id,
        articulo: { id: articulo.id, codigo_alfa: articulo.codigo_alfa, descripcion: articulo.descripcion, codigo_presea: articulo.codigo_presea, factura_como_id: articulo.factura_como_id },
        cantidad: 1,
        precio_lista: sinPrecio ? '' : precio,
        descuento: '',
        observacion: '',
        precio_manual: sinPrecio,
      }]
    }))
    pedirSugerenciaWineClub(articulo)
  }

  const agregarArticulo = (articulo) => {
    const precioEnLista = preciosLista[articulo.id]
    // Sin lista fija de usuario, o lista fija pero el artículo está FUERA de esa
    // lista (sugerencia #23): abrir el modal para elegir precio entre todas las
    // listas — evita cargarlo con precio_manual=true en blanco.
    if (!userLista || precioEnLista == null) { setArticuloPrecios(articulo); return }
    agregarItemConPrecio(articulo, precioEnLista)
  }

  const actualizarItem = (index, campo, valor) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === index ? { ...it, [campo]: valor } : it)
    }))
  }

  const eliminarItem = (index) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== index) }))
  }

  const vaciarItems = () => {
    if (form.items.length === 0) return
    if (!window.confirm(`¿Eliminar los ${form.items.length} artículos de la nota?`)) return
    setForm(f => ({ ...f, items: [] }))
  }

  // C3 — article selected from F2 modal
  const handleArticuloSeleccionado = (articulo) => {
    if (articuloBuscandoIdx === null) return
    const precio = preciosLista[articulo.id]
    const sinPrecio = precio == null
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === articuloBuscandoIdx ? {
        ...it,
        articulo_id: articulo.id,
        articulo: { id: articulo.id, codigo_alfa: articulo.codigo_alfa, descripcion: articulo.descripcion, codigo_presea: articulo.codigo_presea, factura_como_id: articulo.factura_como_id },
        precio_lista: sinPrecio ? it.precio_lista : precio,
        precio_manual: sinPrecio,
      } : it)
    }))
    setArticuloBuscandoIdx(null)
  }

  // C2 — rows confirmed from paste modal
  const handleConfirmPaste = (nuevoItems) => {
    setForm(f => ({ ...f, items: [...f.items, ...nuevoItems] }))
  }

  // C4 — new client created
  const handleClienteCreado = (cliente) => {
    setForm(f => ({ ...f, cliente_id: cliente.id, cliente }))
    setShowAltaCliente(false)
  }

  // Buscar artículos — multi selección (precio de la lista efectiva; vacío → manual)
  // El selector único devuelve [{articulo, cantidad}] (mismo contrato que la NV nueva).
  const handleArticulosMulti = (seleccion) => {
    setForm(f => ({
      ...f,
      items: [...f.items, ...seleccion.map(({ articulo: a, cantidad }) => {
        const precio = preciosLista[a.id]
        const sinPrecio = precio == null
        return {
          articulo_id: a.id,
          articulo: { id: a.id, codigo_alfa: a.codigo_alfa, descripcion: a.descripcion, codigo_presea: a.codigo_presea, factura_como_id: a.factura_como_id },
          cantidad: cantidad || 1,
          precio_lista: sinPrecio ? '' : precio,
          descuento: '',
          observacion: '',
          precio_manual: sinPrecio,
        }
      })]
    }))
  }

  // C2 — button click reads clipboard
  const handlePasteButtonClick = async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (text) { setPasteText(text); setShowPasteModal(true) }
    } catch {
      alert('No se pudo leer el portapapeles. Hacé clic fuera de los campos de texto y presioná Ctrl+V.')
    }
  }

  const calcularTotales = () => {
    const subtotalItems = form.items.reduce((acc, it) => {
      const pf = (parseFloat(it.precio_lista) || 0) * (1 - (parseFloat(it.descuento) || 0) / 100)
      return acc + pf * (parseInt(it.cantidad) || 0)
    }, 0)
    const total = subtotalItems * (1 - (form.descuento_gral || 0) / 100)
    return { subtotalItems: subtotalItems.toFixed(2), total: total.toFixed(2) }
  }

  // Desglose de percepciones EN VIVO mientras se carga la NV (debounced): el
  // vendedor conoce el total que va a facturar Presea sin tener que mandar el
  // paquete ni emitir la proforma. Si el cliente no es RI no se muestra nada.
  useEffect(() => {
    const lineas = form.items
      .filter(it => it.articulo_id && (parseFloat(it.precio_lista) || 0) !== 0 && (parseInt(it.cantidad) || 0) > 0)
      .map(it => ({
        articulo_id: it.articulo_id,
        cantidad: parseInt(it.cantidad) || 0,
        precio_final: (parseFloat(it.precio_lista) || 0) * (1 - (parseFloat(it.descuento) || 0) / 100),
      }))
    if (!form.cliente_id || lineas.length === 0) { setPercep(null); return }
    const t = setTimeout(() => {
      api.post('/notas-venta/percepciones', {
        cliente_id: form.cliente_id,
        descuento_gral: parseFloat(form.descuento_gral) || 0,
        lineas,
      }).then(({ data }) => setPercep(data)).catch(() => setPercep(null))
    }, 600)
    return () => clearTimeout(t)
  }, [form.cliente_id, form.descuento_gral, form.items])

  // Sugerencia del sacacorchos de láminas (sug #58): primera compra de
  // vinos guardados del cliente → alerta al vendedor con un click para
  // agregarlo. La decisión completa (¿lleva VG? ¿nunca compró VG ni tiene
  // sacacorchos? ¿hay stock?) vive en el backend; acá solo se pregunta.
  const [sugVg, setSugVg] = useState(null)
  const [sugVgCerrada, setSugVgCerrada] = useState(false)
  useEffect(() => {
    const ids = form.items.map(it => it.articulo_id).filter(Boolean)
    if (!form.cliente_id || ids.length === 0) { setSugVg(null); return }
    const t = setTimeout(() => {
      api.post('/notas-venta/sugerencia-vg', { cliente_id: form.cliente_id, articulo_ids: ids })
        .then(({ data }) => setSugVg(data.sugerir ? data.articulo : null))
        .catch(() => setSugVg(null))
    }, 600)
    return () => clearTimeout(t)
  }, [form.cliente_id, form.items])
  // Ya lo cargó en esta venta (desde el banner o el buscador): nada que sugerir.
  const sugVgVisible = sugVg && !sugVgCerrada
    && !form.items.some(it => it.articulo?.codigo_alfa === sugVg.codigo_alfa)

  // Aviso Andreani (sugerencia #47), debounced: resuelve la zona de la
  // dirección de ENTREGA con el mismo resolver de logística. Si cae en
  // Interior (más de ~50 km), el vendedor se entera al TOMAR el pedido de
  // que no corresponde transporte propio.
  useEffect(() => {
    const src = form.dirEnvio_usar_cliente
      ? { localidad: form.cliente?.localidad, cp: form.cliente?.codigo_postal }
      : { localidad: form.direccion_envio?.localidad, cp: form.direccion_envio?.codigo_postal }
    if (!src.localidad && !src.cp) { setEnvioFuera(false); return }
    const t = setTimeout(() => {
      api.get('/zonas-resolver', { params: { localidad: src.localidad || '', cp: src.cp || '' } })
        .then(({ data }) => setEnvioFuera(!!data.fuera_reparto_propio))
        .catch(() => setEnvioFuera(false))
    }, 600)
    return () => clearTimeout(t)
  }, [form.dirEnvio_usar_cliente, form.cliente, form.direccion_envio?.localidad, form.direccion_envio?.codigo_postal])

  const buildPayload = () => {
    // dirEnvio_usar_cliente=true → direccion_envio=null (lee del cliente)…
    // salvo que haya RECEPTOR/teléfono cargados: viajan solos (la dirección
    // sigue siendo la del cliente, la recibe otra persona).
    let direccionEnvio = null
    // Retiro en sucursal: la marca del pedido se devuelve TAL CUAL — editar
    // los renglones no cambia cómo se entrega.
    if (form.dirEnvio_retira) {
      direccionEnvio = form.dirEnvio_retira
    } else if (form.dirEnvio_usar_cliente) {
      const receptor = form.direccion_envio?.receptor_nombre?.trim() || null
      const tel = form.direccion_envio?.telefono?.trim() || null
      if (receptor || tel) {
        direccionEnvio = { receptor_nombre: receptor, telefono: tel, calle: null, piso: null, localidad: null, provincia: null, codigo_postal: null }
      }
      // Venía como RETIRO y el operador lo pasó a envío: el backend exige que
      // la conversión sea EXPLÍCITA ({retira:false}) — un null pelado conserva
      // la marca (es lo que la borraba en silencio).
      if (form.direccion_envio?.retira === false) {
        direccionEnvio = { ...(direccionEnvio || {}), retira: false }
      }
    }
    if (!form.dirEnvio_retira && !form.dirEnvio_usar_cliente) {
      const d = form.direccion_envio || {}
      const tieneAlgo = Object.values(d).some(v => v && String(v).trim() !== '')
      if (tieneAlgo) {
        direccionEnvio = {
          receptor_nombre: d.receptor_nombre?.trim() || null,
          telefono:        d.telefono?.trim() || null,
          calle:           d.calle?.trim() || null,
          // El piso faltaba acá: el campo existía en pantalla pero se perdía
          // en silencio al guardar (la dirección es sagrada).
          piso:            d.piso?.trim() || null,
          localidad:       d.localidad?.trim() || null,
          provincia:       d.provincia?.trim() || null,
          codigo_postal:   d.codigo_postal?.trim() || null,
        }
      }
    }
    return {
      cliente_id: form.cliente_id,
      vendedor_id: form.vendedor_id ? parseInt(form.vendedor_id) : null,
      vendedor_codigo: form.vendedor_codigo ? parseInt(form.vendedor_codigo) : null,
      // La lista con la que se cotizó ESTA venta. Define el canal: un cliente
      // de agua (LISTADIST) puede comprar vinos con otra lista, y entonces esa
      // venta ya no es del canal agua.
      lista_precios: listaEfectiva || null,
      fecha_entrega_est: form.fecha_entrega_est || null,
      horario_entrega: form.fecha_entrega_est ? (form.horario_entrega?.trim() || null) : null,
      notas_entrega: form.notas_entrega?.trim() || null,
      indicaciones_packaging: form.indicaciones_packaging?.trim() || null,
      orden_compra_cliente: form.orden_compra_cliente?.trim() || null,
      direccion_envio: direccionEnvio,
      envio_destinos: destinosParaPayload(form.envio_destinos, lineasEnvio),
      condicion_venta: form.condicion_venta ? parseInt(form.condicion_venta) : null,
      deposito: form.deposito ? parseInt(form.deposito) : null,
      observaciones: form.observaciones,
      descuento_gral: parseFloat(form.descuento_gral) || 0,
      es_mkt: !!form.es_mkt,
      items: form.items.map(it => ({
        articulo_id: it.articulo_id,
        cantidad: parseInt(it.cantidad),
        precio_lista: parseFloat(it.precio_lista),
        descuento: parseFloat(it.descuento) || 0,
        observacion: it.observacion || null,
        descripcion: it.descripcion?.trim() || null,
        // El backend borra y recrea los renglones al guardar: si estos campos
        // no viajan, editar la NV los perdía en silencio (turno de una cena,
        // restricción alimentaria del comprador).
        turno: it.turno || null,
        restriccion: it.restriccion || null,
      })),
    }
  }

  const ejecutarGuardar = async () => {
    // Envío múltiple: un reparto que no cierra no se guarda (la planilla del T80 no podría cumplirlo).
    if (form.envio_destinos.length > 0 && !repartoDe(form.envio_destinos, lineasEnvio).exacto) {
      setErrores({ envio_destinos: 'El reparto entre destinos no cierra: cada artículo tiene que quedar asignado exacto.' })
      alert('El reparto del envío múltiple no cierra: cada artículo tiene que quedar asignado exacto entre los destinos.')
      return
    }
    setGuardando(true)
    setErrores({})
    try {
      const payload = buildPayload()
      let nv
      if (esEdicion) {
        const { data } = await notasVentaApi.actualizar(id, payload)
        nv = data
      } else {
        const { data } = await notasVentaApi.crear(payload)
        nv = data
      }
      setMensajeOk(`NV ${nv.numero_lgr} guardada en borrador.`)
      setTimeout(() => navigate(`/notas-venta/${nv.id}`), 1200)
    } catch (err) {
      const data = err.response?.data
      setErrores(data?.errors || { general: [data?.message || 'Error al guardar.'] })
    } finally { setGuardando(false) }
  }

  const ejecutarEnviar = async () => {
    setEnviando(true)
    setErrores({})
    try {
      const payload = buildPayload()
      let nv
      if (esEdicion) {
        const { data } = await notasVentaApi.actualizar(id, payload)
        nv = data
      } else {
        const { data } = await notasVentaApi.crear(payload)
        nv = data
      }
      await notasVentaApi.enviar(nv.id)
      setMensajeOk(`NV ${nv.numero_lgr} encolada para envío a Presea.`)
      setTimeout(() => navigate(`/notas-venta/${nv.id}`), 1200)
    } catch (err) {
      const data = err.response?.data
      setErrores(data?.errors || { general: [data?.message || 'Error al enviar.'] })
    } finally { setEnviando(false) }
  }

  const rowKeyDown = (i) => (e) => {
    if (e.key === 'F2') { e.preventDefault(); setArticuloBuscandoIdx(i) }
  }

  const { subtotalItems, total } = calcularTotales()

  // Estimador de envío ANDREANI (interior): peso por tipo de producto +
  // tarifario negociado + packaging 25% + seguro Andreani (1% de carrito ÷ 1,21 ÷ 2). El desglose lo
  // arma el backend (misma cuenta que cobra el checkout web); el vendedor
  // decide si agrega la línea ENV0000 con ese precio.
  // Líneas para el cotizador de envío (precio con descuento, IVA incluido como en la NV).
  const lineasEnvio = form.items
    .filter(it => it.articulo_id && (parseInt(it.cantidad) || 0) > 0)
    .map(it => ({
      articulo_id: it.articulo_id, sku: it.articulo?.codigo_alfa, descripcion: it.articulo?.descripcion,
      cantidad: parseInt(it.cantidad) || 0,
      precio: (parseFloat(it.precio_lista) || 0) * (1 - (parseFloat(it.descuento) || 0) / 100),
    }))
  const direccionCotizar = form.dirEnvio_usar_cliente
    ? { provincia: form.cliente?.provincia, localidad: form.cliente?.localidad, codigo_postal: form.cliente?.codigo_postal }
    : form.direccion_envio
  // El cotizador devuelve el costo; acá se inserta (o reemplaza) la línea ENV0000 con precio propio.
  const agregarLineaEnvio = ({ costo, detalle, articulo_envio_id }) => setForm(f => ({
    ...f,
    items: [...f.items.filter(it => it.articulo?.codigo_alfa !== 'ENV0000'), {
      articulo_id: articulo_envio_id,
      articulo: { id: articulo_envio_id, codigo_alfa: 'ENV0000', descripcion: detalle },
      // La descripción viaja a la NV (max 120): el papel dice qué envío es, no "ENVIO" a secas.
      cantidad: 1, precio_lista: Math.round(costo * 100) / 100, descuento: '', observacion: '', descripcion: detalle.slice(0, 120), precio_manual: true,
    }],
  }))

  if (cargando) return <p style={{ color: '#64748b' }}>Cargando nota de venta...</p>

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <h2 style={styles.title}>{esEdicion ? 'Editar Nota de Venta' : 'Nueva Nota de Venta'}</h2>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
      </div>

      {mensajeOk && <div style={styles.success}>{mensajeOk}</div>}
      {errores.general && <div style={styles.errorBox}>{errores.general[0]}</div>}

      {/* Nota del checkout: el comprador dejó una instrucción — el vendedor
          la tiene que tener a la vista mientras arma/edita el pedido. */}
      <NotaClienteBanner nota={notaCliente} />

      <div style={styles.grid}>
        {/* Datos generales */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Datos generales</h3>

          <label style={styles.label}>Cliente *</label>
          <div style={styles.clienteRow}>
            <div style={{ flex: 1 }}>
              <BuscadorCliente
                key={form.cliente_id || 'no-client'}
                value={form.cliente}
                onChange={(c) => setForm(f => ({ ...f, cliente_id: c.id, cliente: c }))}
              />
            </div>
            <Boton
              variante="secondary"
              sm
              type="button"
              style={{ flexShrink: 0 }}
              onClick={() => setShowBuscadorClienteModal(true)}
              title="Buscar cliente (F6)"
            >🔍</Boton>
            <Boton
              variante="primary"
              sm
              type="button"
              style={{ flexShrink: 0 }}
              onClick={() => setShowAltaCliente(true)}
              title="Nuevo cliente"
            >+</Boton>
          </div>
          <span style={styles.f6Hint}>F6 abre la búsqueda avanzada (por nombre, CUIT, DNI, localidad o email).</span>
          {form.cliente && (() => {
            const c = form.cliente
            const RESP = { 1: 'Responsable Inscripto', 3.1: 'Monotributo', 4: 'Exento', 5: 'Consumidor Final' }
            const resp = c.tipo_resp != null && c.tipo_resp !== '' ? (RESP[parseFloat(c.tipo_resp)] || `Cond. ${c.tipo_resp}`) : null
            // Misma regla que Cliente::letraFactura(): la letra forzada en la
            // ficha gana; si no hay, se deduce de la condición IVA (RI → A).
            const forzada = (c.tipo_factura || '').toUpperCase()
            const factura = ['A', 'B'].includes(forzada) ? forzada : (parseFloat(c.tipo_resp) === 1 ? 'A' : 'B')
            const dir = [c.domicilio, c.localidad, c.provincia, c.codigo_postal && `CP ${c.codigo_postal}`].filter(Boolean).join(', ')
            const linea = { display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.1rem', fontSize: '0.82rem', color: '#475569', marginTop: 3 }
            return (
              <div style={styles.clienteInfo}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span>{etiquetaCliente(c)}</span>
                  {/* La ficha se corrige acá mismo (el cliente cambió de
                      teléfono/dirección/email por Magento o por teléfono) —
                      sin ir a Clientes. Guarda en la ficha, no en la NV. */}
                  <button
                    type="button"
                    onClick={() => setShowEditarCliente(true)}
                    title="Editar la ficha del cliente (nombre, documento, dirección, email, teléfono, lista, vendedor) sin salir de la NV"
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: '#2563eb', fontSize: '0.78rem', fontWeight: 500, textDecoration: 'underline' }}
                  >
                    ✏️ editar ficha
                  </button>
                </div>
                <div style={linea}>
                  {c.cuit && <span><b>CUIT/Doc:</b> {c.cuit}</span>}
                  {resp && <span><b>IVA:</b> {resp}</span>}
                  <span>
                    <b>Factura:</b> {factura}{' '}
                    <button
                      type="button"
                      onClick={() => setShowCondFiscal(true)}
                      title="Cambiar la condición fiscal del cliente (CUIT, condición IVA, letra de factura)"
                      style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: '#2563eb', fontSize: '0.8rem', textDecoration: 'underline' }}
                    >
                      ✏️ cambiar
                    </button>
                  </span>
                </div>
                {dir && <div style={linea}>📍 {dir}</div>}
                {(c.email || c.telefono) && (
                  <div style={linea}>
                    {c.email && <span>✉ {c.email}</span>}
                    {c.telefono && <span>📞 {c.telefono}</span>}
                  </div>
                )}
                <div style={{ marginTop: 5 }}>
                  {userLista ? (
                    <span style={styles.listaTag} title="Lista asignada a tu usuario">Lista: {userLista} (de tu usuario)</span>
                  ) : (
                    <span style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: 600 }}>
                      💲 Sin lista fija — al agregar un artículo elegís el precio entre todas las listas
                    </span>
                  )}
                  {c.lista_precios && <span style={{ marginLeft: 12, fontSize: '0.78rem', color: '#64748b' }}>Lista del cliente: <b>{c.lista_precios}</b></span>}
                </div>
              </div>
            )
          })()}

          <div style={styles.row2}>
            <div>
              <label style={styles.label}>Vendedor</label>
              <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                <input
                  type="number"
                  min="1"
                  style={{ ...styles.input, width: '5rem', fontFamily: 'monospace' }}
                  placeholder="cód."
                  value={form.vendedor_codigo}
                  onChange={e => setForm(f => ({ ...f, vendedor_codigo: e.target.value, vendedor_nombre: '' }))}
                  onBlur={async () => {
                    const c = parseInt(form.vendedor_codigo)
                    if (!c) return
                    try {
                      const { data } = await api.get(`/vendedores/${c}`)
                      setForm(f => ({ ...f, vendedor_nombre: data.nombre, ...(data.inactivo ? { } : {}) }))
                    } catch {
                      setForm(f => ({ ...f, vendedor_nombre: '(no existe)' }))
                    }
                  }}
                />
                <button
                  type="button"
                  title="Buscar vendedor"
                  onClick={() => setShowVendedoresModal(true)}
                  style={{ padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', background: 'white', borderRadius: '6px', cursor: 'pointer' }}
                >🔍</button>
                <span style={{ fontSize: '0.875rem', color: '#0f172a', fontWeight: 600, flex: 1 }}>
                  {form.vendedor_nombre || ''}
                </span>
              </div>
            </div>
            <div>
              <label style={styles.label}>Fecha</label>
              {/* Solo lectura (Martin, 31 ago 2026): la fecha de la venta es HOY al
                  crear y no se toca al editar — el operador confundía este campo
                  con la fecha de ENTREGA (que tiene su campo propio) y la factura
                  salía fechada a futuro. El backend descarta cualquier fecha. */}
              <input
                style={{ ...styles.input, background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
                type="date"
                value={form.fecha}
                disabled
                title="La fecha de la venta se fija sola (hoy al crear). La fecha de entrega va en su campo propio."
              />
            </div>
            <div>
              <label style={styles.label}>Condición de venta</label>
              <select style={styles.input} value={form.condicion_venta} onChange={e => setForm(f => ({ ...f, condicion_venta: e.target.value }))}>
                <option value="">Elegí una condición…</option>
                {condiciones.map(c => (
                  <option key={c.codigo} value={c.codigo}>{c.descripcion}</option>
                ))}
                {form.condicion_venta && !condiciones.some(c => String(c.codigo) === String(form.condicion_venta)) && (
                  <option value={form.condicion_venta}>Cód. {form.condicion_venta}</option>
                )}
              </select>
            </div>
            <div>
              <label style={styles.label}>Depósito</label>
              <input style={styles.input} type="number" value={form.deposito} onChange={e => setForm(f => ({ ...f, deposito: e.target.value }))} placeholder="100" />
            </div>
          </div>

          {/* Canje MKT — solo administradores. Fija vendedor 1 + sin recibo; el
              modal "Enviar paquete" preselecciona la entrega virtual. */}
          {esAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer', background: form.es_mkt ? '#faf5ff' : '#f8fafc', border: `1px solid ${form.es_mkt ? '#d8b4fe' : '#e2e8f0'}`, borderRadius: 8, padding: '0.5rem 0.75rem' }}>
              <input
                type="checkbox"
                checked={!!form.es_mkt}
                onChange={async e => {
                  const on = e.target.checked
                  setForm(f => ({ ...f, es_mkt: on, ...(on ? { vendedor_codigo: '1', vendedor_nombre: '' } : {}) }))
                  if (on) {
                    try { const { data } = await api.get('/vendedores/1'); setForm(f => ({ ...f, vendedor_nombre: data.nombre })) } catch { /* sin red: queda el código */ }
                  }
                }}
              />
              <span><b>🎯 Canje MKT</b> — sin entrega (virtual), sin recibo, vendedor 1. El paquete a Presea sale con la entrega "Sin entrega" preseleccionada.</span>
            </label>
          )}

          <label style={styles.label}>Observaciones</label>
          <textarea style={styles.textarea} value={form.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} rows={3} placeholder="Ej: TRANSFERENCIA BANCARIA" />

          {/* Entrega + OC del cliente. Lo que se cargue acá viaja al remito
              cuando se arme el paquete; la OC además se mete en obs de la factura. */}
          <div style={{ ...styles.row2, marginTop: '0.75rem' }}>
            <div>
              <label style={styles.label}>Fecha estimada de entrega</label>
              <input style={styles.input} type="date" value={form.fecha_entrega_est} onChange={e => setForm(f => ({ ...f, fecha_entrega_est: e.target.value }))} />
              {/* Debajo de la fecha y sin formato: el horario se acuerda con el
                  cliente y baja al remito tal como se escriba. */}
              <input
                style={{ ...styles.input, marginTop: '0.4rem' }}
                value={form.horario_entrega}
                onChange={e => setForm(f => ({ ...f, horario_entrega: e.target.value }))}
                placeholder="Horario (ej: 12 a 18)"
                maxLength={120}
                title="Se imprime en el remito tal como lo escribas"
              />
            </div>
            <div>
              <label style={styles.label}>Orden de compra del cliente</label>
              <input style={styles.input} value={form.orden_compra_cliente} onChange={e => setForm(f => ({ ...f, orden_compra_cliente: e.target.value }))} placeholder="N° de OC (figura en factura)" maxLength={60} />
            </div>
          </div>

          <label style={styles.label}>Detalles de entrega</label>
          <textarea
            style={styles.textarea}
            value={form.notas_entrega}
            onChange={e => setForm(f => ({ ...f, notas_entrega: e.target.value }))}
            rows={2}
            placeholder="Ej: entregar antes de las 12 hs · timbre fondo · llamar al llegar"
          />
          {/* Indicaciones de packaging para el depósito: salen abajo del
              remito, arriba de la firma (Martin, 15 sep 2026). */}
          <input
            style={{ ...styles.input, width: '100%', marginTop: '0.5rem' }}
            value={form.indicaciones_packaging}
            onChange={e => setForm(f => ({ ...f, indicaciones_packaging: e.target.value }))}
            placeholder="Indicaciones de packaging"
            maxLength={1000}
            title="Se imprime abajo del remito (ENTREGAR CON:), en original y duplicado"
          />

          <div style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
            {form.dirEnvio_retira ? (
              /* El pedido es RETIRO: se dice, y salir de ese modo es un click
                 consciente — no un efecto de guardar (LGR-20260831-TZ9VWJ). */
              <div>
                <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                  🏬 Retira en sucursal{form.dirEnvio_retira.sucursal ? `: ${form.dirEnvio_retira.sucursal}` : ''} — no se despacha a domicilio
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 8 }}>
                  Lo eligió el cliente en el pedido. Guardar la NV no lo cambia.
                </div>
                <Boton variante="ghost" sm onClick={() => setForm(f => ({ ...f, dirEnvio_retira: null, dirEnvio_usar_cliente: true, direccion_envio: { ...DIR_ENVIO_VACIA, retira: false } }))}>
                  Cambiar a envío a domicilio
                </Boton>
              </div>
            ) : (
            <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.5rem' }}>
              <input
                type="checkbox"
                checked={form.dirEnvio_usar_cliente}
                onChange={e => setForm(f => ({ ...f, dirEnvio_usar_cliente: e.target.checked }))}
              />
              Entregar en la dirección del cliente
            </label>
            )}
            {form.dirEnvio_usar_cliente && (
              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>Receptor (si no lo recibe el cliente)</label>
                  <input style={styles.input} value={form.direccion_envio.receptor_nombre} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, receptor_nombre: e.target.value } }))} placeholder="Opcional — nombre de quien recibe" />
                </div>
                <div>
                  <label style={styles.label}>Teléfono del receptor</label>
                  <input style={styles.input} value={form.direccion_envio.telefono} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, telefono: e.target.value } }))} placeholder="Opcional" />
                </div>
              </div>
            )}
            {dirGuardadas.length > 0 && (
              <div style={{ marginBottom: '0.5rem' }}>
                <select
                  style={{ ...styles.input, color: '#475569' }}
                  value=""
                  onChange={e => {
                    const d = dirGuardadas.find(x => String(x.id) === e.target.value)?.direccion
                    if (!d) return
                    setForm(f => ({
                      ...f,
                      dirEnvio_usar_cliente: false,
                      direccion_envio: { ...DIR_ENVIO_VACIA, ...d },
                    }))
                  }}
                >
                  <option value="">📍 Reusar una dirección ya usada con este cliente…</option>
                  {dirGuardadas.map(d => {
                    const dir = d.direccion || {}
                    const linea = [
                      [dir.calle, dir.piso].filter(Boolean).join(' '),
                      dir.localidad,
                    ].filter(Boolean).join(', ')
                    return (
                      <option key={d.id} value={d.id}>
                        {linea}{dir.receptor_nombre ? ` — recibe ${dir.receptor_nombre}` : ''}{d.veces > 1 ? ` (usada ${d.veces} veces)` : ''}
                      </option>
                    )
                  })}
                </select>
              </div>
            )}
            {!form.dirEnvio_usar_cliente && (
              <>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.5rem' }}>
                  Esta dirección reemplaza la del cliente solo para el envío (no toca los datos del cliente).
                </div>
                <div style={styles.row2}>
                  <div>
                    <label style={styles.label}>Receptor (nombre)</label>
                    <input style={styles.input} value={form.direccion_envio.receptor_nombre} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, receptor_nombre: e.target.value } }))} />
                  </div>
                  <div>
                    <label style={styles.label}>Teléfono</label>
                    <input style={styles.input} value={form.direccion_envio.telefono} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, telefono: e.target.value } }))} placeholder="+54 11 1234-5678" />
                  </div>
                </div>
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={styles.label}>Calle y número</label>
                  <CampoDireccion
                    value={form.direccion_envio.calle}
                    onChange={v => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, calle: v } }))}
                    onPlaceSelected={d => setForm(f => ({
                      ...f,
                      direccion_envio: {
                        ...f.direccion_envio,
                        calle: d.calle || f.direccion_envio.calle,
                        // Google sólo devuelve calle y altura. El piso/depto que
                        // el operador escribió se recupera acá; sin esto se
                        // perdía al elegir la sugerencia y el remito salía sin
                        // el "6° B" (caso real, NV LGR-20260805-V7QB5J).
                        piso: f.direccion_envio.piso || d.detalle_extra || '',
                        localidad: d.localidad || f.direccion_envio.localidad,
                        provincia: d.provincia || f.direccion_envio.provincia,
                        codigo_postal: d.codigo_postal || f.direccion_envio.codigo_postal,
                      },
                    }))}
                    inputStyle={styles.input}
                    placeholder="Calle y número"
                  />
                </div>
                <div>
                  {/* Campo PROPIO. Google sólo conoce calle y altura, así que
                      escrito adentro del renglón de arriba se perdía al elegir
                      la sugerencia. Acá sobrevive, y si el operador lo tipeó
                      antes de elegir, se recupera solo. */}
                  <label style={styles.label}>Piso / Depto</label>
                  <input
                    style={styles.input}
                    value={form.direccion_envio.piso}
                    onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, piso: e.target.value } }))}
                    placeholder="Ej: 6° B — o timbre, casa del fondo…"
                  />
                </div>
                {(() => {
                  // Mismo patrón que ABM Clientes / modal "Enviar paquete":
                  // - Provincia siempre selector con las 24 provincias.
                  // - CABA: oculta localidad, CP obligatorio.
                  // - Buenos Aires: localidad con selector de LOCALIDADES_BA.
                  // - Otra: localidad libre.
                  const env = form.direccion_envio
                  const setEnv = (k, v) => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, [k]: v } }))
                  // Provincia/localidad canónicas para que el <select> muestre el
                  // valor guardado aunque venga en mayúsculas/sin tildes (#24).
                  const provinciaCanon = matchProvincia(env.provincia)
                  const esCabaEnv = esCapitalFederal(provinciaCanon)
                  return (
                    <div style={{ ...styles.row2, marginTop: '0.5rem' }}>
                      <div>
                        <label style={styles.label}>Provincia</label>
                        <select style={styles.input} value={provinciaCanon} onChange={e => {
                          // Al cambiar provincia, limpio localidad (si era de otra).
                          const nueva = e.target.value
                          setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, provincia: nueva, localidad: esCapitalFederal(nueva) ? '' : f.direccion_envio.localidad } }))
                        }}>
                          <option value="">— Provincia —</option>
                          {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      {!esCabaEnv && (
                        <div>
                          <label style={styles.label}>Localidad</label>
                          <CampoLocalidad
                            provincia={provinciaCanon}
                            value={env.localidad}
                            onChange={v => setEnv('localidad', v)}
                            inputStyle={styles.input}
                            cp={env.codigo_postal}
                          />
                        </div>
                      )}
                      <div style={{ flex: esCabaEnv ? 1 : 0.5 }}>
                        <label style={styles.label}>Código postal{esCabaEnv ? ' *' : ''}</label>
                        <input style={styles.input} value={env.codigo_postal} onChange={e => setEnv('codigo_postal', e.target.value)} required={esCabaEnv} />
                      </div>
                    </div>
                  )
                })()}
              </>
            )}
            {envioFuera && (
              <div style={{ marginTop: '0.6rem', background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', fontWeight: 500 }}>
                ⚠ Entrega a más de ~50 km (zona Interior): no corresponde transporte propio — el envío va por <b>Andreani</b>.
              </div>
            )}

            {/* El cotizador único (uno o varios destinos) — misma cuenta que la tienda. */}
            <CotizadorEnvio
              lineas={lineasEnvio}
              direccion={direccionCotizar}
              destinos={form.envio_destinos}
              onDestinosChange={d => setForm(f => ({ ...f, envio_destinos: d }))}
              onAgregarLinea={agregarLineaEnvio}
            />
          </div>
        </div>

        {/* Totales */}
        <div style={styles.totalesCard}>
          <h3 style={styles.cardTitle}>Totales</h3>
          <div style={styles.totalRow}>
            <span>Artículos</span>
            <strong>{textoConteo(conteoItems(form.items))}</strong>
          </div>
          <div style={styles.totalRow}>
            <span>Subtotal ítems</span>
            <strong>${fmtMonto(subtotalItems)}</strong>
          </div>
          <div style={styles.totalRow}>
            <label style={styles.labelInline}>Descuento gral. (%)</label>
            <input
              style={{ ...styles.input, width: '80px', textAlign: 'right' }}
              type="number" min="0" max="100" step="0.0001"
              value={form.descuento_gral}
              onChange={e => setForm(f => ({ ...f, descuento_gral: e.target.value }))}
            />
          </div>
          <div style={{ ...styles.totalRow, borderTop: '2px solid #1e293b', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>TOTAL</span>
            <strong style={{ fontSize: '1.25rem', color: '#1e40af' }}>
              ${fmtMonto(total)}
            </strong>
          </div>
          {percep?.aplica && (
            <>
              {percep.percep_iva > 0 && (
                <div style={{ ...styles.totalRow, fontSize: '0.85rem', color: '#475569' }}>
                  <span>Percep. IVA</span>
                  <span>${fmtMonto(percep.percep_iva)}</span>
                </div>
              )}
              {percep.percep_iibb > 0 && (
                <div style={{ ...styles.totalRow, fontSize: '0.85rem', color: '#475569' }}>
                  <span>Percep. IIBB CABA{percep.alicuota_iibb ? ` (${percep.alicuota_iibb}%)` : ''}</span>
                  <span>${fmtMonto(percep.percep_iibb)}</span>
                </div>
              )}
              <div style={styles.totalRow}>
                <span style={{ fontWeight: 600 }}>Total c/ percepciones</span>
                <strong style={{ color: '#1e40af' }}>${fmtMonto((parseFloat(total) || 0) + percep.total)}</strong>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.4 }}>
                Estimado con la condición fiscal registrada del cliente ({percep.condicion || 'RI'}) — el importe final lo fija Presea al facturar.
              </div>
            </>
          )}
          <div style={{ marginTop: '1rem' }}>
            <Boton
              variante="primary"
              style={{ width: '100%' }}
              disabled={guardando || !form.cliente_id || form.items.length === 0}
              onClick={() => ejecutarGuardar()}
            >
              {guardando ? 'Guardando...' : esEdicion ? '💾 Guardar cambios' : '💾 Crear nota de venta'}
            </Boton>
          </div>
        </div>
      </div>

      {/* Artículos */}
      <div style={styles.card}>
        <div style={styles.articulosHeader}>
          <h3 style={styles.cardTitle}>Artículos</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {form.items.length > 0 && (
              <Boton
                variante="secondary" sm
                onClick={consultarStock}
                disabled={stockPresea.cargando}
                title="Consulta el stock EN VIVO en Presea, por depósito, de los artículos cargados"
              >
                {stockPresea.cargando ? '⏳ Consultando…' : '📦 Consultar stock'}
              </Boton>
            )}
            {form.items.length > 0 && (
              <Boton
                variante="secondary" sm
                onClick={() => setShowPedirDeposito(true)}
                title="Le pregunta a depósito si hay los productos que elijas; la respuesta vuelve a esta NV"
              >
                🏬 Pedir stock a depósito
              </Boton>
            )}
            <Boton variante="secondary" sm onClick={() => setShowBuscarArticulos(true)} title="Buscar y agregar varios artículos">
              Buscar artículos
            </Boton>
            <Boton variante="secondary" sm onClick={handlePasteButtonClick} title="Pegar filas copiadas desde Excel (Ctrl+V)">
              📋 Pegar desde Excel
            </Boton>
            {form.items.length > 0 && (
              <Boton variante="destructive" sm onClick={vaciarItems} title="Eliminar todos los artículos de la nota">
                🗑 Vaciar
              </Boton>
            )}
          </div>
        </div>

        {/* Sug #58: primera compra de vinos guardados → sugerir el sacacorchos
            de láminas. Un corcho con años de guarda se rompe con el espiral. */}
        {sugVgVisible && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: 8, padding: '8px 12px', margin: '0.6rem 0', fontSize: '0.85rem' }}>
            <span style={{ flex: 1 }}>
              🍷 <b>Primera compra de vinos guardados de este cliente.</b> Sugerile el
              sacacorchos de láminas — un corcho con años de guarda sale entero con láminas,
              no con espiral. <span style={{ whiteSpace: 'nowrap' }}>{sugVg.descripcion}{sugVg.precio != null ? ` · $ ${Number(sugVg.precio).toLocaleString('es-AR')}` : ''}</span>
            </span>
            <Boton variante="secondary" sm onClick={() => agregarItemConPrecio(sugVg, sugVg.precio)}>
              ➕ Agregar
            </Boton>
            <button onClick={() => setSugVgCerrada(true)} title="No sugerir en esta venta"
              style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
        )}

        {sugerenciaWc && (
          <div style={styles.sugerenciaWc}>
            <span style={styles.sugerenciaWcTxt}>
              <b>Wine Club {sugerenciaWc.nombre}.</b>{' '}
              {sugerenciaWc.items.length > 0 ? (
                <>
                  La selección de <b>{sugerenciaWc.etiqueta}</b> lleva {sugerenciaWc.items.length}{' '}
                  {sugerenciaWc.items.length === 1 ? 'vino' : 'vinos'}: {sugerenciaWc.items.map(i => i.descripcion).join(' · ')}.
                  {!sugerenciaWc.del_mes && <em style={styles.sugerenciaWcOjo}> Ojo: es la selección de un mes anterior, la de este mes todavía no está confirmada.</em>}
                </>
              ) : sugerenciaWc.motivo}
            </span>
            <span style={styles.sugerenciaWcAcc}>
              {sugerenciaWc.items.length > 0 && (
                <Boton variante="primary" sm onClick={sumarVinosDelClub}>Agregar los vinos (100% desc.)</Boton>
              )}
              <Boton variante="ghost" sm onClick={() => setSugerenciaWc(null)}>No, gracias</Boton>
            </span>
          </div>
        )}
        <BuscadorArticulo onSelect={agregarArticulo} precios={preciosLista} clienteId={form.cliente_id} lista={listaParaBuscar(listaEfectiva)} />

        {/* Acá había un cartel de "artículos para revisar" que aparecía al
            cargar los renglones. Se sacó a pedido de Martin (6 ago 2026): en el
            form molesta y no es el momento de resolverlo — se está cargando el
            pedido, no facturándolo.

            El aviso sigue donde importa: en el detalle de la NV, al abrir
            "Enviar paquete a Presea", que es cuando el código faltante
            realmente frena. Y el freno duro está en el backend
            (EnviarNVAPreseaJob): un ítem sin código ni alias vinculado nunca
            viaja, así que sacar el cartel no deja pasar nada. */}

        {stockPresea.error && (
          <div style={{ margin: '0.5rem 0', fontSize: '0.8125rem', color: '#991b1b', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 10px' }}>
            {stockPresea.error}
          </div>
        )}

        {form.items.length === 0 ? (
          <p style={styles.emptyItems}>Buscá artículos arriba, usá el botón "Pegar desde Excel" o presioná Ctrl+V.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.itemTable}>
              <thead>
                <tr>
                  {['Código', 'Descripción', 'Cantidad',
                    // La columna de stock aparece recién después de consultar.
                    ...(stockPresea.porCodigo ? ['Stock'] : []),
                    ...(deposito.hay ? ['Depósito'] : []),
                    'Precio lista', 'Desc. %', 'Precio final', 'Subtotal', ''].map(h => (
                    <th key={h} style={styles.itemTh}>
                      {h === 'Stock' ? <CabeceraStockPresea depositos={stockPresea.depositos} /> : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => {
                  const pf = (parseFloat(it.precio_lista) || 0) * (1 - (parseFloat(it.descuento) || 0) / 100)
                  const subtotal = pf * (parseInt(it.cantidad) || 0)
                  const tieneDescuento = pf < it.precio_lista
                  // "⚠ Sin precio" es SOLO para el renglón que de verdad no
                  // tiene precio (el campo está vacío). Un renglón con
                  // precio_manual pero precio cargado es un PRECIO PROPIO
                  // (promo web horneada, envío cotizado, gift card, precio
                  // negociado): mostrarle "sin precio — ingresá manual" al
                  // editar invitaba a pisar un precio que estaba bien
                  // (incidente NV 1YH2GX, 11/08/2026).
                  const sinPrecio = it.precio_manual === true
                    && (it.precio_lista === '' || it.precio_lista == null || Number.isNaN(it.precio_lista))
                  const precioPropio = it.precio_manual === true && ! sinPrecio
                  const esGift = (it.articulo?.codigo_alfa || '').toUpperCase().startsWith('GIF')
                  const rowBg = sinPrecio ? '#fff7ed' : (tieneDescuento ? '#fefce8' : undefined)
                  return (
                    <tr key={i} style={rowBg ? { background: rowBg } : {}}>
                      <td style={styles.itemTd}>
                        <div style={styles.codigoCell}>
                          <code style={{ fontSize: '0.8125rem' }}>{it.articulo?.codigo_alfa}</code>
                          <button
                            style={styles.lupaBtn}
                            onClick={() => setArticuloBuscandoIdx(i)}
                            title="Cambiar artículo (F2)"
                          >🔍</button>
                        </div>
                      </td>
                      <td style={styles.itemTd}>
                        {/* Detalle propio del renglón: pisa la descripción del
                            maestro en factura e impresos (clientes que no
                            pueden hacer figurar su compra). El artículo, el
                            precio y el stock no cambian. */}
                        {it.editandoDetalle ? (
                          <input
                            style={{ ...styles.inputSm, width: '100%' }}
                            autoFocus
                            maxLength={120}
                            value={it.descripcion || ''}
                            placeholder={it.articulo?.descripcion}
                            onChange={e => actualizarItem(i, 'descripcion', e.target.value)}
                            onBlur={() => actualizarItem(i, 'editandoDetalle', false)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); actualizarItem(i, 'editandoDetalle', false) } }}
                          />
                        ) : (
                          <>
                            {it.descripcion?.trim() ? (
                              <span title={`Detalle propio — el artículo real es: ${it.articulo?.descripcion}`}>
                                {it.descripcion}
                                <span style={{ marginLeft: 6, fontSize: '0.68rem', color: '#7c3aed', background: '#f5f3ff', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>detalle propio</span>
                              </span>
                            ) : it.articulo?.descripcion}
                            <button
                              style={{ ...styles.lupaBtn, marginLeft: 6 }}
                              title="Cambiar el detalle que figura en factura e impresos (no cambia el artículo)"
                              onClick={() => actualizarItem(i, 'editandoDetalle', true)}
                            >✏️</button>
                          </>
                        )}
                        {sinPrecio && (
                          <div style={styles.warnSinPrecio}>⚠ Sin precio en lista — ingresá manual.</div>
                        )}
                        {precioPropio && (
                          <div style={styles.notaPrecioPropio} title="Este renglón tiene un precio fijado al cargarse (promo web, envío cotizado, gift card o precio negociado). No se recotiza contra la lista al abrir la nota.">
                            Precio propio — no se recotiza con la lista.
                          </div>
                        )}
                      </td>
                      <td style={styles.itemTd}>
                        <input style={styles.inputSm} type="number" value={it.cantidad}
                          onChange={e => actualizarItem(i, 'cantidad', e.target.value === '' ? '' : parseInt(e.target.value))}
                          onKeyDown={rowKeyDown(i)} />
                      </td>
                      {stockPresea.porCodigo && (
                        <CeldaStockPresea porCodigo={stockPresea.porCodigo} depositos={stockPresea.depositos} codigo={it.articulo?.codigo_alfa} cantidad={it.cantidad} style={styles.itemTd} />
                      )}
                      {deposito.hay && (
                        <td style={styles.itemTd}><RespuestaDeposito item={deposito.porArticulo[it.articulo_id]} /></td>
                      )}
                      <td style={styles.itemTd}>
                        <input style={styles.inputSm} type="number" min={esGift ? undefined : '0'} step="0.01" value={it.precio_lista}
                          onChange={e => actualizarItem(i, 'precio_lista', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          onKeyDown={rowKeyDown(i)}
                          title={esGift ? 'Gift card: admite precio negativo (descuento/ajuste)' : undefined} />
                      </td>
                      <td style={styles.itemTd}>
                        <input style={{ ...styles.inputSm, ...(tieneDescuento ? { borderColor: '#f59e0b' } : {}) }}
                          type="number" min="0" max="100" step="0.0001" value={it.descuento}
                          onChange={e => actualizarItem(i, 'descuento', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          onKeyDown={rowKeyDown(i)} />
                      </td>
                      <td style={{ ...styles.itemTd, fontWeight: 600, color: tieneDescuento ? '#b45309' : '#166534' }}>
                        ${fmtMonto(pf)}
                      </td>
                      <td style={styles.itemTd}>${fmtMonto(subtotal)}</td>
                      <td style={styles.itemTd}>
                        <Boton variante="destructive" sm onClick={() => eliminarItem(i)}>✕</Boton>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mensajes del pedido — el mismo hilo del detalle, también mientras se
          edita el borrador ("en cualquier estado" incluye este momento). */}
      {esEdicion && <MensajesNVCard nvId={id} />}

      {/* C3 — Modal selector de artículo (F2 / lupa) */}
      {articuloBuscandoIdx !== null && (
        <SelectorArticulosModal
          modo="simple"
          onSelect={handleArticuloSeleccionado}
          onClose={() => setArticuloBuscandoIdx(null)}
          precios={preciosLista}
          clienteId={form.cliente_id}
          lista={listaParaBuscar(listaEfectiva)}
        />
      )}

      {/* C2 — Modal paste from Excel */}
      {showPasteModal && (
        <PasteExcelModal
          clipboardText={pasteText}
          precios={preciosLista}
          onConfirm={handleConfirmPaste}
          onClose={() => { setShowPasteModal(false); setPasteText('') }}
        />
      )}

      {/* Condición fiscal del cliente (CUIT / cond. IVA / letra) sin salir de la NV */}
      {showCondFiscal && form.cliente && (
        <ModalCondicionFiscal
          cliente={form.cliente}
          onClose={() => setShowCondFiscal(false)}
          onSaved={(c) => { setForm(f => ({ ...f, cliente: c })); setShowCondFiscal(false) }}
        />
      )}

      {/* Ficha completa del cliente, editable sin salir de la NV */}
      {showEditarCliente && form.cliente && (
        <ModalAltaCliente
          cliente={form.cliente}
          onClose={() => setShowEditarCliente(false)}
          onGuardado={(c) => { setForm(f => ({ ...f, cliente_id: c.id, cliente: c })); setShowEditarCliente(false) }}
        />
      )}

      {/* C4 — Modal alta cliente */}
      {showAltaCliente && (
        <ModalAltaCliente
          onClose={() => setShowAltaCliente(false)}
          onCreado={handleClienteCreado}
        />
      )}

      {/* Buscar artículos multi-selección */}
      {showPedirDeposito && (
        <PedirStockDepositoModal
          nvId={id}
          renglones={form.items.map(it => ({ articulo_id: it.articulo_id, codigo_alfa: it.articulo?.codigo_alfa, descripcion: it.articulo?.descripcion, cantidad: it.cantidad }))}
          onClose={() => setShowPedirDeposito(false)}
          onCreada={() => deposito.recargar()}
        />
      )}

      {showBuscarArticulos && (
        <SelectorArticulosModal
          onConfirm={handleArticulosMulti}
          onClose={() => setShowBuscarArticulos(false)}
          precios={preciosLista}
          clienteId={form.cliente_id}
          lista={listaParaBuscar(listaEfectiva)}
        />
      )}

      {/* F6 — Lista de clientes */}
      {showBuscadorClienteModal && (
        <ModalListaClientes
          onSelect={(c) => {
            setForm(f => ({ ...f, cliente_id: c.id, cliente: c }))
            setShowBuscadorClienteModal(false)
          }}
          onClose={() => setShowBuscadorClienteModal(false)}
        />
      )}

      {showVendedoresModal && (
        <ModalListaVendedores
          onSelect={(v) => setForm(f => ({ ...f, vendedor_codigo: String(v.codigo), vendedor_nombre: v.nombre }))}
          onClose={() => setShowVendedoresModal(false)}
        />
      )}

      {/* Modal asignar lista al cliente (cliente sin lista) */}
      {articuloPrecios && (
        <ModalPreciosArticulo
          articulo={articuloPrecios}
          onElegir={(precio) => { agregarItemConPrecio(articuloPrecios, precio); setArticuloPrecios(null) }}
          onClose={() => setArticuloPrecios(null)}
        />
      )}
    </div>
  )
}

function ModalAsignarLista({ clienteNombre, onConfirm, onClose }) {
  const [listas, setListas] = useState([])
  const [codigo, setCodigo] = useState('')
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    listasApi.listar().then(({ data }) => {
      const items = Array.isArray(data) ? data : (data.data || [])
      setListas(items)
    }).catch(() => setListas([]))
  }, [])

  const confirmar = async () => {
    if (!codigo) return
    setGuardando(true)
    try {
      await onConfirm(codigo)
    } finally { setGuardando(false) }
  }

  return (
    <Modal title="Asignar lista de precios al cliente" onClose={onClose} maxWidth="440px">
      <p style={{ margin: '0 0 1rem', color: '#475569', fontSize: '0.9rem' }}>
        <strong>{clienteNombre}</strong> no tiene lista de precios asignada. Elegí una para continuar — los artículos
        ya cargados serán recalculados con los precios de la nueva lista.
      </p>
      <label style={{ display: 'block', fontWeight: '600', fontSize: '0.8125rem', color: '#374151', marginBottom: '0.25rem' }}>Lista *</label>
      <select
        autoFocus
        style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
        value={codigo} onChange={e => setCodigo(e.target.value)}
      >
        <option value="">— Seleccionar —</option>
        {listas.map(l => (
          <option key={l.id} value={l.codigo_presea || l.codigo}>
            {l.nombre} {(l.codigo_presea || l.codigo) ? `(${l.codigo_presea || l.codigo})` : ''}
          </option>
        ))}
      </select>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" onClick={confirmar} disabled={!codigo || guardando}>
          {guardando ? 'Asignando...' : 'Asignar y recalcular'}
        </Boton>
      </div>
    </Modal>
  )
}

const styles = {
  sugerenciaWc: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', marginBottom: 10 },
  sugerenciaWcTxt: { flex: 1, minWidth: 260, fontSize: 13, color: '#0f172a', lineHeight: 1.45 },
  sugerenciaWcOjo: { color: '#92400e', fontStyle: 'normal' },
  sugerenciaWcAcc: { display: 'flex', gap: 8, marginLeft: 'auto' },
  wrap: { maxWidth: '1200px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' },
  title: { margin: 0, fontSize: '1.25rem', color: '#1e293b' },
  success: { background: '#dcfce7', color: '#166534', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontWeight: '600' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 280px', gap: '1rem', marginBottom: '1rem' },
  card: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  totalesCard: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', gap: '0.75rem', alignSelf: 'start', position: 'sticky', top: '1rem' },
  cardTitle: { margin: '0 0 0.25rem', fontSize: '0.9375rem', fontWeight: '700', color: '#1e293b' },
  label: { fontWeight: '600', fontSize: '0.875rem', color: '#374151' },
  labelInline: { fontWeight: '600', fontSize: '0.875rem', color: '#374151', whiteSpace: 'nowrap' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9375rem', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9375rem', boxSizing: 'border-box', resize: 'vertical' },
  row2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' },
  clienteRow: { display: 'flex', gap: '0.5rem', alignItems: 'flex-start' },
  clienteInfo: { background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.875rem', color: '#0c4a6e', display: 'flex', flexDirection: 'column', gap: '2px' },
  listaTag: { fontSize: '0.75rem', color: '#0369a1', fontWeight: '600' },
  f6Hint: { fontSize: '0.75rem', color: '#64748b', marginTop: '-0.25rem' },
  warnSinPrecio: { marginTop: '4px', fontSize: '0.75rem', color: '#9a3412', fontWeight: 600 },
  notaPrecioPropio: { marginTop: '4px', fontSize: '0.72rem', color: '#64748b', fontWeight: 500 },
  totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.9rem' },
  articulosHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  emptyItems: { color: '#94a3b8', textAlign: 'center', padding: '1rem 0', fontSize: '0.875rem' },
  itemTable: { width: '100%', borderCollapse: 'collapse', marginTop: '0.75rem', fontSize: '0.875rem' },
  itemTh: { padding: '0.5rem 0.75rem', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '700', fontSize: '0.8125rem', color: '#64748b', whiteSpace: 'nowrap' },
  itemTd: { padding: '0.5rem 0.5rem', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  codigoCell: { display: 'flex', alignItems: 'center', gap: '4px' },
  lupaBtn: { padding: '0.1rem 0.3rem', background: 'none', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', lineHeight: 1, color: '#64748b', flexShrink: 0 },
  inputSm: { padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem', width: '80px', boxSizing: 'border-box' },
}
